import React, { useState, useCallback, useEffect } from 'react';
import {
  PlotSizingCalculator,
  PlotSizingResults,
} from '../utils/PlotSizingCalculator';
import FormOnlyStepContainer from '../components/step-flow/FormOnlyStep';
import * as Yup from 'yup';
import getOption from '../utils/helpers/getOption';
import PlotVisualizer from '../components/global/PlotVisualizer/PlotVisualizer';
import { GPS_TYPES } from '../components/create-trial/EquipmentInformationForm';
import './plot-sizing-calculator.scss';

// Type for form values
interface FormValues {
  executionEquipmentSwathWidth: number;
  executionEquipmentSwathWidthUnits: string;
  executionEquipmentRateChangeDistance: number;
  executionEquipmentRateChangeDistanceUnits: string;
  executionEquipmentCanSplitSwath: boolean;
  executionEquipmentDgpsType: string;
  executionEquipmentDgpsAccuracy?: number;
  executionEquipmentDgpsAccuracyUnits?: string;
  harvesterSwathWidth: number;
  harvesterSwathWidthUnits: string;
  harvesterSpatialResolution: number;
  harvesterSpatialResolutionUnits: string;
  harvesterDgpsType: string;
  harvesterDgpsAccuracy?: number;
  harvesterDgpsAccuracyUnits?: string;
  treatmentDrift: number;
  treatmentDriftUnits: string;
  maxLikelyHarvestAngle: number;
}

const initialValues = {
  executionEquipmentSwathWidth: 30,
  executionEquipmentSwathWidthUnits: 'ft',
  executionEquipmentRateChangeDistance: 50,
  executionEquipmentRateChangeDistanceUnits: 'ft',
  executionEquipmentCanSplitSwath: false,
  executionEquipmentDgpsType: 'WAAS', // Default to WAAS
  harvesterSwathWidth: 25,
  harvesterSwathWidthUnits: 'ft',
  harvesterSpatialResolution: 50,
  harvesterSpatialResolutionUnits: 'ft',
  harvesterDgpsType: 'WAAS', // Default to WAAS
  treatmentDrift: 5,
  treatmentDriftUnits: 'ft',
  maxLikelyHarvestAngle: 0,
};

const PlotSizingCalculatorPage: React.FC = () => {
  const [results, setResults] = useState<PlotSizingResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<any>(null);

  // State for form values - shared between both containers
  const [sharedFormValues, setSharedFormValues] = useState(initialValues);

  // Calculate results whenever form values change
  useEffect(() => {
    if (!formValues) return;

    try {
      // Convert GPS types to accuracy values for the calculator
      const calculatorConfig = convertFormValuesToCalculatorConfig(formValues);
      const calculator = new PlotSizingCalculator(calculatorConfig);
      const calculatedResults = calculator.calculatePlotSizing();
      setResults(calculatedResults);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setResults(null);
    }
  }, [formValues]);

  // Calculate initial results
  useEffect(() => {
    setFormValues(initialValues);
  }, []);

  // Unified callback that updates shared state and calculations
  const handleValuesChange = useCallback((values: Partial<FormValues>) => {
    // Update shared state with new values using functional update
    setSharedFormValues((prev) => {
      const updatedValues = { ...prev, ...values };
      // Update calculations immediately with the new values
      setFormValues(updatedValues);
      return updatedValues;
    });
  }, []); // No dependencies to avoid infinite loop

  // Helper function to convert form values to calculator config
  const convertFormValuesToCalculatorConfig = (values: FormValues) => {
    // Get execution equipment GPS accuracy
    const executionGpsConfig = GPS_TYPES[values.executionEquipmentDgpsType];
    let executionAccuracy = executionGpsConfig?.accuracy || 0;
    let executionAccuracyUnits = executionGpsConfig?.units || 'm';

    // Use custom values if "Other" or no match
    if (!executionGpsConfig && values.executionEquipmentDgpsAccuracy) {
      executionAccuracy = values.executionEquipmentDgpsAccuracy;
      executionAccuracyUnits =
        values.executionEquipmentDgpsAccuracyUnits || 'm';
    }

    // Get harvester GPS accuracy
    const harvesterGpsConfig = GPS_TYPES[values.harvesterDgpsType];
    let harvesterAccuracy = harvesterGpsConfig?.accuracy || 0;
    let harvesterAccuracyUnits = harvesterGpsConfig?.units || 'm';

    // Use custom values if "Other" or no match
    if (!harvesterGpsConfig && values.harvesterDgpsAccuracy) {
      harvesterAccuracy = values.harvesterDgpsAccuracy;
      harvesterAccuracyUnits = values.harvesterDgpsAccuracyUnits || 'm';
    }

    return {
      executionEquipmentSwathWidth: values.executionEquipmentSwathWidth,
      executionEquipmentSwathWidthUnits:
        values.executionEquipmentSwathWidthUnits as 'ft' | 'm',
      executionEquipmentRateChangeDistance:
        values.executionEquipmentRateChangeDistance,
      executionEquipmentRateChangeDistanceUnits:
        values.executionEquipmentRateChangeDistanceUnits as 'ft' | 'm',
      executionEquipmentCanSplitSwath: values.executionEquipmentCanSplitSwath,
      executionEquipmentDgpsAccuracy: executionAccuracy,
      executionEquipmentDgpsAccuracyUnits: executionAccuracyUnits as 'ft' | 'm',
      harvesterSwathWidth: values.harvesterSwathWidth,
      harvesterSwathWidthUnits: values.harvesterSwathWidthUnits as 'ft' | 'm',
      harvesterSpatialResolution: values.harvesterSpatialResolution,
      harvesterSpatialResolutionUnits:
        values.harvesterSpatialResolutionUnits as 'ft' | 'm',
      harvesterDgpsAccuracy: harvesterAccuracy,
      harvesterDgpsAccuracyUnits: harvesterAccuracyUnits as 'ft' | 'm',
      treatmentDrift: values.treatmentDrift,
      treatmentDriftUnits: values.treatmentDriftUnits as 'ft' | 'm',
      maxLikelyHarvestAngle: values.maxLikelyHarvestAngle,
    };
  };

  const validationSchema = Yup.object({
    executionEquipmentSwathWidth: Yup.number()
      .positive('Must be positive')
      .required('Execution equipment swath width is required'),
    executionEquipmentRateChangeDistance: Yup.number().required(
      'Rate change distance is required',
    ),
    executionEquipmentDgpsType: Yup.string().required(
      'Execution equipment GPS type is required',
    ),
    harvesterSwathWidth: Yup.number()
      .positive('Must be positive')
      .required('Harvester swath width is required'),
    harvesterSpatialResolution: Yup.number().required(
      'Spatial resolution is required',
    ),
    harvesterDgpsType: Yup.string().required('Harvester GPS type is required'),
    treatmentDrift: Yup.number()
      .min(0, 'Cannot be negative')
      .required('Treatment drift is required'),
    treatmentDriftUnits: Yup.string()
      .oneOf(['ft', 'm'], 'Invalid unit')
      .required('Treatment drift units are required'),
    maxLikelyHarvestAngle: Yup.number()
      .min(0, 'Cannot be negative')
      .max(45, 'Must be 45 degrees or less')
      .required('Max harvest angle is required'),
  });

  const unitOptions = [getOption('ft', 'ft'), getOption('m', 'm')];
  const gpsTypeOptions = Object.entries(GPS_TYPES).map(([value, config]) =>
    getOption(value, config.label),
  );

  return (
    <div className="plot-sizing-calculator">
      {/* Split pane layout */}
      <div className="plot-sizing-calculator__split-pane">
        {/* Left side - Execution Equipment */}
        <div className="plot-sizing-calculator__form-section plot-sizing-calculator__form-section--execution-equipment">
          <h3 className="plot-sizing-calculator__section-title plot-sizing-calculator__section-title--execution">
            Execution Equipment
          </h3>
          <FormOnlyStepContainer
            uniqueKey="execution-equipment"
            formElements={[
              {
                element: 'horizontalInputPair',
                elements: [
                  {
                    name: 'executionEquipmentSwathWidth',
                    label: 'Swath Width:',
                    type: 'number',
                    step: '0.1',
                  },
                  {
                    element: 'radioGroup',
                    name: 'executionEquipmentSwathWidthUnits',
                    radioGroupHeader: 'Unit:',
                    value: 'ft',
                    options: unitOptions,
                  },
                ],
              },
              {
                element: 'horizontalInputPair',
                elements: [
                  {
                    name: 'executionEquipmentRateChangeDistance',
                    label: 'Rate Change Distance:',
                    type: 'number',
                    step: '0.1',
                  },
                  {
                    element: 'radioGroup',
                    name: 'executionEquipmentRateChangeDistanceUnits',
                    radioGroupHeader: 'Unit:',
                    value: 'ft',
                    options: unitOptions,
                  },
                ],
              },
              {
                element: 'horizontalInputPair',
                elements: [
                  {
                    name: 'treatmentDrift',
                    label: 'Treatment Drift:',
                    type: 'number',
                    step: '0.1',
                  },
                  {
                    element: 'radioGroup',
                    name: 'treatmentDriftUnits',
                    radioGroupHeader: 'Unit:',
                    value: 'ft',
                    options: unitOptions,
                  },
                ],
              },
              {
                element: 'horizontalSingleInput',
                inputElement: {
                  element: 'select',
                  name: 'executionEquipmentDgpsType',
                  label: 'GPS Type:',
                  options: gpsTypeOptions,
                },
              },
              {
                element: 'horizontalSingleInput',
                inputElement: {
                  element: 'checkbox',
                  name: 'executionEquipmentCanSplitSwath',
                  label: 'Machine can split swath 50/50',
                },
              },
            ]}
            initialValues={sharedFormValues}
            validationSchema={validationSchema}
            valuesCallback={handleValuesChange}
          />
        </div>

        {/* Right side - Harvester */}
        <div className="plot-sizing-calculator__form-section plot-sizing-calculator__form-section--harvester">
          <h3 className="plot-sizing-calculator__section-title plot-sizing-calculator__section-title--harvester">
            Harvester
          </h3>
          <FormOnlyStepContainer
            uniqueKey="harvester"
            formElements={[
              {
                element: 'horizontalInputPair',
                elements: [
                  {
                    name: 'harvesterSwathWidth',
                    label: 'Swath Width:',
                    type: 'number',
                    step: '0.1',
                  },
                  {
                    element: 'radioGroup',
                    name: 'harvesterSwathWidthUnits',
                    radioGroupHeader: 'Unit:',
                    value: 'ft',
                    options: unitOptions,
                  },
                ],
              },
              {
                element: 'horizontalInputPair',
                elements: [
                  {
                    name: 'harvesterSpatialResolution',
                    label: 'Spatial Resolution:',
                    type: 'number',
                    step: '0.1',
                  },
                  {
                    element: 'radioGroup',
                    name: 'harvesterSpatialResolutionUnits',
                    radioGroupHeader: 'Unit:',
                    value: 'ft',
                    options: unitOptions,
                  },
                ],
              },
              {
                element: 'horizontalInputPair',
                elements: [
                  {
                    name: 'maxLikelyHarvestAngle',
                    label: 'Max Likely Harvest Angle:',
                    type: 'number',
                    step: '0.1',
                    min: '0',
                    max: '45',
                  },
                  {
                    element: 'staticText',
                    name: 'harvest-angle-units',
                    label: 'Unit:',
                    value: '°',
                    displayValue: '°',
                  },
                ],
              },
              {
                element: 'horizontalSingleInput',
                inputElement: {
                  element: 'select',
                  name: 'harvesterDgpsType',
                  label: 'GPS Type:',
                  options: gpsTypeOptions,
                },
              },
            ]}
            initialValues={sharedFormValues}
            validationSchema={validationSchema}
            valuesCallback={handleValuesChange}
          />
        </div>
      </div>

      {/* Results Section */}
      {error && (
        <div className="plot-sizing-calculator__error">
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {results && (
        <div className="plot-sizing-calculator__results">
          {/* Left side - Calculated Dimensions */}
          <div className="plot-sizing-calculator__dimensions">
            <div className="plot-sizing-calculator__dimensions-container">
              <h3 className="plot-sizing-calculator__dimensions-title">
                Plot Dimensions
              </h3>

              <div className="plot-sizing-calculator__dimensions-grid">
                <div className="plot-sizing-calculator__dimensions-card">
                  <div className="plot-sizing-calculator__dimensions-card-label">
                    Plot Length
                  </div>
                  <div className="plot-sizing-calculator__dimensions-card-value">
                    {results.suggestedPlotLengthFt.toFixed(1)} ft
                  </div>
                </div>

                <div className="plot-sizing-calculator__dimensions-card">
                  <div className="plot-sizing-calculator__dimensions-card-label">
                    Plot Width
                  </div>
                  <div className="plot-sizing-calculator__dimensions-card-value">
                    {results.suggestedPlotWidthFt.toFixed(1)} ft
                  </div>
                </div>

                <div className="plot-sizing-calculator__dimensions-card">
                  <div className="plot-sizing-calculator__dimensions-card-label">
                    Buffer Width
                  </div>
                  <div className="plot-sizing-calculator__dimensions-card-value">
                    {results.suggestedPlotBufferWidthFt.toFixed(1)} ft
                  </div>
                </div>

                <div className="plot-sizing-calculator__dimensions-card">
                  <div className="plot-sizing-calculator__dimensions-card-label">
                    Buffer Length
                  </div>
                  <div className="plot-sizing-calculator__dimensions-card-value">
                    {results.suggestedPlotBufferLengthFt.toFixed(1)} ft
                  </div>
                </div>

                <div className="plot-sizing-calculator__dimensions-card">
                  <div className="plot-sizing-calculator__dimensions-card-label">
                    Field Buffer Width
                  </div>
                  <div className="plot-sizing-calculator__dimensions-card-value">
                    {results.suggestedFieldBufferWidthFt.toFixed(1)} ft
                  </div>
                </div>

                <div className="plot-sizing-calculator__dimensions-card">
                  <div className="plot-sizing-calculator__dimensions-card-label">
                    80 acre field
                  </div>
                  <div className="plot-sizing-calculator__dimensions-card-value">
                    ~{(results.plotsPerAcre * 80).toFixed(0)} plots
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Plot Visualizer */}
          <div className="plot-sizing-calculator__visualizer">
            <PlotVisualizer results={results} formValues={formValues} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotSizingCalculatorPage;
